import styled from "styled-components";
import TypingResponse from "./TypingResponse";
import AnswerActions from "./AnswerActions";
import { getAnswerReferences } from "../utils/getAnswerReferences";
import { useState } from "react";

const AnswerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  color: var(--chat-color);
`;

// `pre-wrap` preserva as quebras de linha que o backend manda no corpo (o HTML
// as colapsaria). Mesma razão do estilo equivalente em TypingResponse.jsx.
const Body = styled.span`
  white-space: pre-wrap;
`;

const Reference = styled.div`
  display: flex;
  flex-direction: column;

  font-size: 0.75rem;
  border-radius: 1rem;
  padding: 0.5rem;

  border: 1px solid var(--color-border);

  max-width: fit-content;
`;

/**
 * Renders a Benthos icon, a message and its references and some action buttons.
 *
 * @param {object} message - An object with the message and the read status
 * @returns {JSX.Element} A `span` element displaying the message text if it has been read
 * or a `TypingResponse` component simulating a typing effect if unread.
 */
function Answer({ message }) {
  const [typingInProgress, setTypingInProgress] = useState(false);
  const { author, source, text, citation, disclosure } = getAnswerReferences(
    message.message
  );

  // Copia TUDO: corpo, citação e rodapé. O rodapé é o que informa quem lê que o
  // texto veio de IA, e copiar é justamente quando o texto sai da página e
  // deixa o aviso permanente atrás — num documento ou e-mail não há aviso fixo
  // nenhum. É por isso que ele sai da TELA mas não da CÓPIA. Mesmo argumento
  // pra citação: trecho sem fonte é o que a curadoria do acervo existe pra
  // evitar.
  //
  // Vai a citação CRUA (`[fonte: ...]`), não a fonte nua, pra preservar o
  // idioma do marcador que o backend escolheu. O rodapé vai SEM os underscores,
  // que em documento e e-mail sairiam como caractere literal.
  const reference = citation || [source, author].filter(Boolean).join(" — ");
  const copyText = [text, reference, disclosure].filter(Boolean).join("\n\n");

  return (
    <AnswerContainer>
      {/* Renders a message conditionally based on its read status */}
      {message?.read ? (
        <Body data-testid="answer-body">{text}</Body>
      ) : (
        <TypingResponse
          message={text}
          setTypingInProgress={setTypingInProgress}
        />
      )}

      {/* O rodapé do Art. 50 NÃO é renderizado. Na web a divulgação é o aviso
          permanente embaixo do campo de entrada (`DialogInput`), que fica em
          tela o tempo todo — inclusive ANTES da primeira pergunta, que é o que
          o Art. 50 pede ("no mais tardar no momento da primeira interação"). O
          rodapé por mensagem só aparecia depois da primeira resposta, e a
          superfície web é em inglês inteira, então o aviso fixo informa a
          audiência que existe. No WhatsApp o rodapé por mensagem continua,
          porque lá não há aviso fixo.

          Ele continua sendo EXTRAÍDO pelo parser: sem isso volta a cair dentro
          do corpo, que é exatamente o defeito que o PR #1 consertou. E continua
          indo em `copyText`, porque o texto copiado sai da página e deixa o
          aviso permanente atrás.

          Referência e ações voltaram a um único gate de `typingInProgress`. Os
          dois gates separados existiam só pra o rodapé caber FORA do gate, que
          era exigência do Art. 50 enquanto ele era renderizado. Sem o rodapé em
          tela, a exceção não tem motivo. */}
      {!typingInProgress && (
        <>
          {(author || source) && (
            <Reference data-testid="answer-reference">
              {source && <i>{source}</i>}
              {author && <i>{author}</i>}
            </Reference>
          )}

          <AnswerActions copyText={copyText} speakText={text} />
        </>
      )}
    </AnswerContainer>
  );
}

export default Answer;
